import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get("session_id");
  const paymentId = searchParams.get("payment_id");

  useEffect(() => {
    const completePayment = async () => {
      if (!paymentId) {
        setProcessing(false);
        return;
      }

      try {
        // Update payment status to completed
        const { error: updateError } = await supabase
          .from("sponsorship_payments")
          .update({ 
            status: "completed",
            completed_at: new Date().toISOString()
          })
          .eq("id", paymentId);

        if (updateError) {
          console.error("Error updating payment status:", updateError);
        }

        // Send notification email
        const { error: notifyError } = await supabase.functions.invoke(
          "send-payment-notification",
          { body: { paymentId } }
        );

        if (notifyError) {
          console.error("Error sending notification:", notifyError);
        }
      } catch (err) {
        console.error("Error completing payment:", err);
        setError("There was an issue processing your payment confirmation.");
      } finally {
        setProcessing(false);
      }
    };

    completePayment();
  }, [paymentId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <Helmet>
        <title>Payment Successful - BuildWithChrist.ai</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-md w-full text-center space-y-6">
        {processing ? (
          <>
            <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
            <h1 className="text-2xl font-bold font-display">Processing...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </>
        ) : error ? (
          <>
            <div className="h-16 w-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold font-display">Payment Received</h1>
            <p className="text-muted-foreground">
              Your payment was received, but there was a minor issue with the confirmation. 
              Our team has been notified.
            </p>
          </>
        ) : (
          <>
            <div className="h-20 w-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto">
              <CheckCircle className="h-12 w-12 text-secondary" />
            </div>
            <h1 className="text-3xl font-bold font-display">Thank You!</h1>
            <p className="text-lg text-muted-foreground">
              Your generous support helps us build AI tools that serve those most in need.
            </p>
            <div className="bg-card border border-border rounded-xl p-6 text-left space-y-3">
              <h3 className="font-semibold">What happens next?</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-secondary">✓</span>
                  You'll receive a receipt at your email address
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary">✓</span>
                  Our team will reach out with sponsor benefits (if applicable)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary">✓</span>
                  Your contribution goes directly to supporting nonprofits
                </li>
              </ul>
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
          <Button asChild>
            <Link to="/">
              Return Home <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/sponsors">View Sponsorship Tiers</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
